import React, { useMemo, useState } from 'react';
import { BookBtn, CSPLogo, TableData, TableRow, SbChip } from '../styles';
import { Grid, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { CSPChargePropsType } from '../../../../store/reducers/GetRates/Type';
import ChargesBreakup from '../ChargesBreakup';
import { getDutyCharge } from '../../../../utils/pricing';
import Modal from '../../../../components/Modal';
import Image from '../../../../components/Image';
import CreateShipmentContext from '../../../../context/CreateShipment';
import { createShipmentContextType } from '../../../../types/shipment';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import SbButton from '@Components/Button';
import { BackToLoginModal } from '../BackToLoginModal';

interface HurricaneData {
  currency?: string | null;
  duty?: {
    amount?: number;
    currency?: string;
  };
  tax?: {
    amount?: number;
    currency?: string;
  };
  products?: Array<{
    description: string;
    dutyRate: number;
    taxRate: number;
  }>;
}

interface SingleCSPMobileProps extends CSPChargePropsType {
  hurricaneData?: HurricaneData;
  dutyChargeAmount?: number;
}

function SingleCSPMobile(props: SingleCSPMobileProps) {
  const { CSPCharge, handleBookCSP, isSelected, hurricaneData, dutyChargeAmount } = props;

  const getDutyTaxDisplay = (data: HurricaneData | undefined): string | null => {
    if (!data) return null;
    const hasDuty = data.duty && typeof data.duty.amount === 'number';
    const hasTax = data.tax && typeof data.tax.amount === 'number';
    if (!hasDuty && !hasTax) return null;

    const dutyAmount = data.duty && typeof data.duty.amount === 'number' ? data.duty.amount : 0;
    const taxAmount = data.tax && typeof data.tax.amount === 'number' ? data.tax.amount : 0;
    const total = dutyAmount + taxAmount;
    const currencyVal = data.duty?.currency || data.tax?.currency || data.currency || 'USD';
    return `${total.toFixed(2)} ${currencyVal}`;
  };

  const displayDutyTax = getDutyTaxDisplay(hurricaneData);
  const { transitTime, serviceName, serviceImage, totalPricing, fastest, cheapest, pricing, taxPricing } = CSPCharge;
  const [showBreakup, setShowBreakup] = useState<boolean>(false);
  const { token } = useSelector((state: RootStateType) => state.loginUser);
  const shipmentContext = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const orgCountryObj = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const orgDefaultCurrency = useSelector((state: RootStateType) => state.orgSetup?.organizationData?.currency?.name);

  const [openBackToLoginModal, setBackToLoginModal] = useState(false);
  const currency = useMemo(
    () => shipmentContext?.packageData?.currency || orgDefaultCurrency || orgCountryObj?.currency?.name || '',
    [shipmentContext, orgCountryObj, orgDefaultCurrency],
  );

  function onClose() {
    setShowBreakup(false);
  }

  const bookCSP = (CSPCharge: CSPChargePropsType['CSPCharge'] | null) => {
    if (token?._id) {
      handleBookCSP(CSPCharge);
    } else {
      setBackToLoginModal(true);
    }
  };
  const handleCloseBackToLoginModal = () => {
    setBackToLoginModal(false);
  };

  return (
    <>
      <TableRow container item alignItems={'center'} xs={12}>
        <TableData container item justifyContent="end" alignItems="flex-end" direction="column" xs={12}>
          {fastest && (
            <SbChip
              className="xs-text font-semibold"
              size="small"
              sx={{
                color: '#127E44',
                background: '#ADFFB5',
                '& .MuiChip-icon': {
                  color: '#127E44',
                },
              }}
              icon={<BoltIcon />}
              label="Fastest"
            />
          )}

          {cheapest && (
            <SbChip
              className="xs-text font-semibold"
              size="small"
              sx={{
                color: '#12647E',
                background: '#ADE6FF',
                '& .MuiChip-icon': {
                  color: '#12647E',
                },
              }}
              icon={<AccountBalanceWalletIcon />}
              label="Cheapest"
            />
          )}
        </TableData>
        <Grid
          container
          item
          xs={12}
          alignItems={'center'}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <TableData item sx={{ flexShrink: 0, marginRight: '8px' }}>
            <CSPLogo>
              <img src={serviceImage} height={'100%'} width={'100%'} style={{ borderRadius: '10px' }} />
            </CSPLogo>
          </TableData>
          <TableData item sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, marginRight: '8px' }}>
            <Grid container>
              <Grid item xs={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Typography
                  className="md-text font-semibold"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {transitTime} Days
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Typography
                  className="xs-text"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {serviceName}
                </Typography>
              </Grid>
            </Grid>
          </TableData>
          <TableData item sx={{ flexShrink: 0, minWidth: '70px', marginRight: '8px', textAlign: 'right' }}>
            <Grid container alignItems={'center'} justifyContent={'end'}>
              <Typography className="md-text font-semibold">
                {`${currency} ${parseFloat(totalPricing?.toString())?.toFixed(2)}`}
              </Typography>
            </Grid>
          </TableData>
          {displayDutyTax && (
            <TableData item sx={{ flexShrink: 0, minWidth: '80px', textAlign: 'right' }}>
              <Grid container alignItems={'center'} justifyContent={'end'}>
                <Typography className="md-text font-semibold text-center w-full">{displayDutyTax}</Typography>
              </Grid>
            </TableData>
          )}
        </Grid>
        <TableData
          container
          item
          xs={12}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{ background: '#F3F3F3', padding: '13px' }}
        >
          <Grid
            container
            item
            xs={6}
            alignItems={'center'}
            textAlign={'center'}
            style={{ cursor: 'default' }}
            onClick={() => setShowBreakup(true)}
          >
            <Image src={'/images/Boxes/Help.svg'} alt="help" style={{ height: '14px', width: '14px' }} />
            <Typography style={{ textDecoration: 'underline' }} className="ml-1">
              View Breakup
            </Typography>
          </Grid>
          <Grid container item xs={6} justifyContent={'end'}>
            {isSelected ? (
              <SbButton variant="contained" onClick={() => bookCSP(null)}>
                Selected
              </SbButton>
            ) : (
              <BookBtn variant="outlined" className="md-text font-semibold" onClick={() => bookCSP(CSPCharge)}>
                Book Now
              </BookBtn>
            )}
          </Grid>
        </TableData>
      </TableRow>
      <Modal open={showBreakup} onClose={onClose}>
        <ChargesBreakup
          pricing={pricing}
          totalPricing={totalPricing}
          taxPricing={taxPricing}
          currency={currency}
          dutyChargeAmount={getDutyCharge(pricing) || dutyChargeAmount}
        />
      </Modal>
      <BackToLoginModal open={openBackToLoginModal} onClose={handleCloseBackToLoginModal} />
    </>
  );
}

export default SingleCSPMobile;
